/**
 * UI Store
 * Zustand store for UI state — theme, toasts, panels, sidebar.
 */

import { create, type StateCreator } from 'zustand';
import { persist } from 'zustand/middleware';
import { createGuid } from '@bizfirst/common-js';

export type Theme = 'dark' | 'light';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

export interface UIState {
  theme: Theme;
  sidebarCollapsed: boolean;
  toasts: Toast[];

  // Actions
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

// Track toast auto-dismiss timers so they can be cancelled on manual removal / clear
const toastTimers = new Map<string, ReturnType<typeof setTimeout>>();

const createUIStore: StateCreator<UIState, [], [], UIState> = (set, get) => ({
  theme: 'dark',
  sidebarCollapsed: false,
  toasts: [],

  setTheme: (theme) => set({ theme }),

  toggleTheme: () =>
    set((s) => ({ theme: s.theme === 'dark' ? 'light' : 'dark' })),

  setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

  toggleSidebar: () =>
    set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

  addToast: (toast) => {
    const id = createGuid();
    const newToast: Toast = { ...toast, id };
    set((s) => ({ toasts: [...s.toasts, newToast] }));

    const duration = toast.duration ?? 4000;
    if (duration > 0) {
      const timer = setTimeout(() => {
        get().removeToast(id);
      }, duration);
      toastTimers.set(id, timer);
    }
  },

  removeToast: (id) => {
    const timer = toastTimers.get(id);
    if (timer) { clearTimeout(timer); toastTimers.delete(id); }
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },

  clearToasts: () => {
    toastTimers.forEach((timer) => clearTimeout(timer));
    toastTimers.clear();
    set({ toasts: [] });
  },
});

export const useUIStore = create<UIState>()(
  persist(createUIStore, {
    name: 'atlas-forms-ui-store',
    version: 3,
    migrate: (persisted: any, fromVersion: number) => {
      // v3: default theme changed to 'dark' — only set if user has no persisted preference
      if (fromVersion < 3 && !persisted.theme) {
        return { ...persisted, theme: 'dark' };
      }
      return persisted;
    },
    partialize: (state) => ({
      theme: state.theme,
      sidebarCollapsed: state.sidebarCollapsed,
    }),
  })
);
